import { useEffect, useRef, useState } from 'react';
import { Icons } from '../Icons';
import BlockedSiteHelp from './BlockedSiteHelp';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  startDomainAudit,
  getDomainAuditStatus,
  cancelDomainAudit,
  getDomainAuditReport,
  generateDomainAuditHTML,
  buildDomainAuditFilename,
  downloadHTMLFile
} from '../../utils/domainAudit';

const POLL_MS = 3000;

// One-button two-phase domain audit: sweep (up to 10 pages × dominant
// device × consent × 1 run) → deep matrix on the 2–3 worst pages
// (2 devices × CMP states × repeated runs). Progress is polled from the
// server; the final report is rendered purely from stored run data.
const DomainAuditPanel = ({ url, disabled = false }) => {
  const { language } = useLanguage();
  const cs = language === 'cs';

  const [state, setState] = useState('idle'); // idle | starting | running | done | failed | cancelled
  const [auditId, setAuditId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [report, setReport] = useState(null);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const finish = async (id, finalStatus) => {
    stopPolling();
    if (finalStatus === 'completed') {
      try {
        const rep = await getDomainAuditReport(id);
        if (!mountedRef.current) return;
        setReport(rep);
        setState('done');
      } catch (e) {
        if (!mountedRef.current) return;
        setError(cs ? 'Report se nepodařilo načíst.' : 'Failed to load the report.');
        setState('failed');
      }
    } else if (finalStatus === 'cancelled') {
      setState('cancelled');
    } else {
      setError(cs ? 'Audit selhal.' : 'The audit failed.');
      setState('failed');
    }
  };

  const poll = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const status = await getDomainAuditStatus(id);
        if (!mountedRef.current) return;
        setProgress(status.progress || null);
        if (status.status !== 'running') await finish(id, status.status);
      } catch (e) {
        // transient poll errors are ignored; the next tick retries
      }
    }, POLL_MS);
  };

  const handleStart = async () => {
    if (state === 'starting' || state === 'running') return;
    setError(null);
    setBlocked(false);
    setReport(null);
    setProgress(null);
    setState('starting');
    try {
      const { auditId: id, resumed } = await startDomainAudit(url);
      if (!mountedRef.current) return;
      setAuditId(id);
      setState('running');
      if (resumed) setError(cs ? 'Navazuji na již běžící audit.' : 'Resuming an already running audit.');
      poll(id);
    } catch (e) {
      if (!mountedRef.current) return;
      setBlocked(e.message === 'site_unreachable');
      setError(e.message === 'premium_required'
        ? (cs ? 'Kompletní audit je dostupný od tarifu Premium.' : 'The full audit is available from the Premium plan.')
        : e.message === 'site_unreachable'
          ? (cs
            ? 'Cílový web neodpovídá — je nedostupný, nebo blokuje automatizované měření. Audit se proto nespustil. Zkuste to později.'
            : 'The target site is not responding — it is unavailable or blocks automated measurement. The audit was not started. Try again later.')
          : (cs ? 'Audit se nepodařilo spustit.' : 'Failed to start the audit.'));
      setState('idle');
    }
  };

  const handleCancel = async () => {
    if (!auditId) return;
    await cancelDomainAudit(auditId).catch(() => {});
    // the poll loop will observe the cancelled status
  };

  const handleDownload = () => {
    if (!report) return;
    const html = generateDomainAuditHTML(report, language);
    downloadHTMLFile(html, buildDomainAuditFilename(report.audit?.domain));
  };

  const phaseLabel = () => {
    const p = progress || {};
    if (p.phase === 'crawl' || state === 'starting') {
      return cs ? 'Hledám stránky domény…' : 'Discovering domain pages…';
    }
    if (p.phase === 'sweep') {
      return cs
        ? `Fáze 1/2 — rychlý sweep: stránka ${Math.min((p.done || 0) + 1, p.total || 0)} z ${p.total || '?'}`
        : `Phase 1/2 — quick sweep: page ${Math.min((p.done || 0) + 1, p.total || 0)} of ${p.total || '?'}`;
    }
    if (p.phase === 'deep') {
      const dev = p.device === 'desktop' ? 'Desktop' : (cs ? 'Mobil' : 'Mobile');
      const cmp = p.cmpMode === 'accept' ? (cs ? 'se souhlasem' : 'consent') : (cs ? 'bez souhlasu' : 'no consent');
      return cs
        ? `Fáze 2/2 — hloubkový test: běh ${Math.min((p.done || 0) + 1, p.total || 0)} z ${p.total || '?'} (${dev}, ${cmp})`
        : `Phase 2/2 — deep test: run ${Math.min((p.done || 0) + 1, p.total || 0)} of ${p.total || '?'} (${dev}, ${cmp})`;
    }
    return cs ? 'Probíhá…' : 'Running…';
  };

  const pct = () => {
    const p = progress || {};
    if (!p.total) return p.phase === 'deep' ? 50 : 5;
    const frac = Math.min(1, (p.done || 0) / p.total);
    // sweep ≈ first 40 %, deep ≈ remaining 60 %
    if (p.phase === 'sweep') return Math.round(5 + frac * 35);
    if (p.phase === 'deep') return Math.round(40 + frac * 60);
    if (p.phase === 'done') return 100;
    return 5;
  };

  const running = state === 'starting' || state === 'running';

  return (
    <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 mb-4">
      <div className="flex items-start gap-3 mb-2">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500/30 to-blue-500/30 flex items-center justify-center border border-cyan-500/40 flex-shrink-0">
          <Icons.Globe size={16} className="text-cyan-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">
            {cs ? 'Samostatný průzkum domény (sweep → deep)' : 'Separate domain survey (sweep → deep)'}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            {cs
              ? 'Jedno tlačítko: rychlý sweep až 10 stránek na převažujícím zařízení, poté hloubková matice (mobil + desktop, s cookie souhlasem i bez, opakované běhy s mediánem) jen na 2–3 nejhorších stránkách. Počítejte s ~15–30 minutami.'
              : 'One button: a quick sweep of up to 10 pages on the dominant device, then a deep matrix (mobile + desktop, with and without cookie consent, repeated runs with medians) on the 2–3 worst pages only. Expect ~15–30 minutes.'}
          </div>
        </div>
      </div>

      {running && (
        <div className="mt-3">
          <div className="flex items-center gap-2 text-xs text-cyan-400 mb-1.5">
            <span className="inline-block w-3 h-3 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            <span>{phaseLabel()}</span>
          </div>
          {progress?.currentUrl && (
            <div className="text-[11px] text-slate-500 truncate mb-1.5">{progress.currentUrl}</div>
          )}
          <div className="w-full h-1.5 rounded-full bg-slate-700 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all" style={{ width: `${pct()}%` }} />
          </div>
        </div>
      )}

      {error && (
        <div className="mt-2 text-xs text-amber-400">{error}</div>
      )}
      {blocked && <BlockedSiteHelp cs={cs} url={url} />}
      {state === 'cancelled' && (
        <div className="mt-2 text-xs text-slate-400">{cs ? 'Audit byl zrušen.' : 'The audit was cancelled.'}</div>
      )}
      {state === 'done' && report && (
        <div className="mt-2 text-xs text-green-400">
          {cs
            ? `Hotovo — ${report.disclosure?.totalRuns ?? '?'} běhů${report.disclosure?.failedRuns ? `, ${report.disclosure.failedRuns} nezměřeno` : ''}.`
            : `Done — ${report.disclosure?.totalRuns ?? '?'} runs${report.disclosure?.failedRuns ? `, ${report.disclosure.failedRuns} unmeasured` : ''}.`}
        </div>
      )}

      <div className="flex items-center gap-2 mt-3">
        {!running && state !== 'done' && (
          <button
            onClick={handleStart}
            disabled={disabled}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 transition-opacity disabled:opacity-40"
          >
            <Icons.Zap size={14} />
            {cs ? 'Spustit průzkum domény' : 'Run domain survey'}
          </button>
        )}
        {running && (
          <button
            onClick={handleCancel}
            className="px-4 py-2 rounded-lg text-sm text-slate-300 border border-slate-600 hover:bg-slate-700 transition-colors"
          >
            {cs ? 'Zrušit audit' : 'Cancel audit'}
          </button>
        )}
        {state === 'done' && report && (
          <>
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-green-500/15 text-green-300 border border-green-500/40 hover:bg-green-500/25 transition-colors"
            >
              <Icons.Download size={14} />
              {cs ? 'Stáhnout report' : 'Download report'}
            </button>
            <button
              onClick={handleStart}
              className="px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
            >
              {cs ? 'Spustit znovu' : 'Run again'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DomainAuditPanel;
